/**
 * DataGemCard — the compact, VISUAL-FIRST gem atom (Johan 2026-08-01:
 * "small individual cards, less text-driven, dramatize the number").
 *
 * Anatomy (one card, ~72px tall):
 *
 *   ┌──────────┬───────────────────────────────┐
 *   │  BIG     │ KICKER            Home – Away │
 *   │  5/6     │ caption sentence (small)      │
 *   │  ▮▮▮▮▮▯  │ market-chip            CTA →  │
 *   └──────────┴───────────────────────────────┘
 *
 * The HERO block dramatizes the finding's own number (engine owns the
 * facts; nothing is fabricated): streak → "5/6" + tick rail · rank →
 * "#1" + /N · rate → big value + subject/baseline bars. The brand's
 * `brand-graphic` motif (Claude Design kit) washes the card corner as a
 * low-opacity watermark, so each brand's gems are unmistakable.
 *
 * Variants (`variant` prop — one component, never a fork):
 *   feed     — lobby stream (default; crest + match line + CTA)
 *   attached — sits directly ON its market's odds section (no CTA, no
 *              self-link — the odds are the next element)
 *   inline   — inside article bodies (full column, tight)
 *
 * Placement is caller-owned; the atom stays dumb.
 */

import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { DataGemSlot, DisplayMarket } from '@aamp/shared-types';
import { useTranslation } from 'react-i18next';
import { buildInsightPhrase, type TranslateFn } from './insightPhrase.js';
import type { MatchInsight, MatchInsightParts } from '../../api/createEngineClient.js';
import { stripMarketToDisplay } from '../../odds/stripMarketsToDisplay.js';
import { useAdapterStrings } from '../../odds/useAdapterStrings.js';
import OddsMarket from '../particles/OddsMarket.js';
import { ErrorBoundary } from '../particles/ErrorBoundary.js';
import { useTenantConfig } from '../../tenant/useTenantConfig.js';
import { useBrandMotif } from '../../tenant/brandAssets.js';
import { TeamCrest } from '../primitives/TeamCrest.js';
import styles from './DataGemCard.module.css';

export type GemVariant = 'feed' | 'attached' | 'inline';

export interface GemAtom {
  strength: number;
  mode: string;
  marketId: string;
  marketLabel: string;
  rationale: string;
  parts: Record<string, unknown> | null;
  homeTeam?: string;
  awayTeam?: string;
  homeBadge?: string;
  awayBadge?: string;
}

interface DataGemCardProps {
  /** Deep-link target event; empty string → render unlinked (match page). */
  eventId: string;
  gem: GemAtom;
  variant?: GemVariant;
}

/** The dramatized number block. Null when the parts can't honestly back
 *  a visual — the card then renders text-led (never a fabricated graphic). */
function GemHero({ parts }: { parts: MatchInsightParts | null }): ReactElement | null {
  if (!parts) return null;
  if (
    typeof parts.count === 'number'
    && typeof parts.ofN === 'number'
    && parts.ofN > 0
    && parts.ofN <= 12
  ) {
    return (
      <span className={styles.hero}>
        <span className={styles.heroNum}>
          {parts.count}
          <span className={styles.heroDen}>/{parts.ofN}</span>
        </span>
        <span className={styles.ticks} aria-hidden="true">
          {Array.from({ length: parts.ofN }, (_, i) => (
            <span key={i} className={i < parts.count! ? styles.tickHit : styles.tickMiss} />
          ))}
        </span>
      </span>
    );
  }
  if (typeof parts.rank === 'number' && typeof parts.rankOf === 'number' && parts.rankOf > 0) {
    return (
      <span className={styles.hero}>
        <span className={styles.heroNum}>
          #{parts.rank}
          <span className={styles.heroDen}>/{parts.rankOf}</span>
        </span>
      </span>
    );
  }
  if (
    typeof parts.value === 'number'
    && typeof parts.baseline === 'number'
    && Number.isFinite(parts.value)
    && Number.isFinite(parts.baseline)
  ) {
    const max = Math.max(Math.abs(parts.value), Math.abs(parts.baseline));
    if (max <= 0) return null;
    const w = (n: number): string => `${Math.max(10, Math.round((Math.abs(n) / max) * 100))}%`;
    const fmt = (n: number): string =>
      Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '');
    return (
      <span className={styles.hero}>
        <span className={styles.heroNum}>{fmt(parts.value)}</span>
        <span className={styles.heroBars} aria-hidden="true">
          <span className={styles.heroBarValue} style={{ width: w(parts.value) }} />
          <span className={styles.heroBarBase} style={{ width: w(parts.baseline) }} />
        </span>
        <span className={styles.heroBaseline}>{fmt(parts.baseline)}</span>
      </span>
    );
  }
  return null;
}

export function DataGemCard({
  eventId,
  gem,
  variant = 'feed',
}: DataGemCardProps): ReactElement | null {
  const { t, i18n } = useTranslation();
  const watermark = useBrandMotif('brand-graphic');

  const insight: MatchInsight = {
    strength: gem.strength,
    mode: gem.mode,
    marketId: gem.marketId,
    marketLabel: gem.marketLabel,
    rationale: gem.rationale,
    parts: (gem.parts as MatchInsight['parts']) ?? null,
  };
  const sentence = buildInsightPhrase(insight, t as unknown as TranslateFn, i18n.language);
  if (!sentence || sentence.trim().length === 0) return null;

  const marketTitle = t(`market.${gem.marketId}.title`, {
    defaultValue: gem.marketLabel,
  });
  const matchLine =
    variant === 'feed' && gem.homeTeam && gem.awayTeam
      ? `${gem.homeTeam} – ${gem.awayTeam}`
      : null;
  // Crest of the finding's SUBJECT team when it names one of the sides.
  const subject = insight.parts?.subject ?? '';
  const crestSrc =
    subject && subject === gem.homeTeam
      ? gem.homeBadge
      : subject && subject === gem.awayTeam
        ? gem.awayBadge
        : undefined;

  const wmStyle: CSSProperties | undefined = watermark
    ? { backgroundImage: `url(${watermark})` }
    : undefined;

  const body = (
    <>
      {watermark ? <span className={styles.watermark} style={wmStyle} aria-hidden="true" /> : null}
      <GemHero parts={insight.parts} />
      <span className={styles.main}>
        <span className={styles.head}>
          <span className={styles.kicker}>{t('gem.kicker')}</span>
          {matchLine ? <span className={styles.match}>{matchLine}</span> : null}
          {crestSrc && subject ? (
            <span className={styles.crest}>
              <TeamCrest src={crestSrc} name={subject} size={16} />
            </span>
          ) : null}
        </span>
        <span className={styles.sentence}>{sentence}</span>
        <span className={styles.foot}>
          <span className={styles.marketChip} title={marketTitle}>
            {marketTitle}
          </span>
          {variant === 'feed' ? <span className={styles.cta}>{t('gem.cta')}</span> : null}
        </span>
      </span>
    </>
  );

  if (variant === 'feed' && eventId) {
    return (
      <Link
        to={`/match/${encodeURIComponent(eventId)}?market=${encodeURIComponent(gem.marketId)}`}
        className={styles.card}
        data-variant={variant}
        data-testid="data-gem-card"
      >
        {body}
      </Link>
    );
  }
  return (
    <div className={styles.card} data-variant={variant} data-testid="data-gem-card">
      {body}
    </div>
  );
}

/**
 * DataGemPair — the gem atom with its bound market's odds card as an
 * adjacent sibling (lobby stream). Unpriced gem → atom stands alone.
 */
export function DataGemPair({
  eventId,
  gem,
}: {
  eventId: string;
  gem: DataGemSlot['gem'];
}): ReactElement | null {
  const { t } = useTranslation();
  const tenant = useTenantConfig();
  // The catalogue's own outcome words. Without them a strip that arrives
  // without labels renders "Yes"/"No" on a Danish page (live defect,
  // /news 2026-09-08) — the keys have existed in all three locales
  // since the odds vocabulary migration; nothing was passing them in.
  const labels = useAdapterStrings();
  const decimalSeparator: '.' | ',' =
    tenant.kind === 'live' || tenant.kind === 'paused'
      ? tenant.config.market.decimalSeparator
      : '.';
  let display: DisplayMarket | null = null;
  if (gem.odds) {
    try {
      display = stripMarketToDisplay(gem.odds as never, { labels });
    } catch {
      display = null;
    }
  }
  const card = <DataGemCard eventId={eventId} gem={gem} variant="feed" />;
  if (!display) return card;
  const marketTitle = t(`market.${gem.marketId}.title`, {
    defaultValue: gem.marketLabel,
  });
  return (
    <div className={styles.pair}>
      {card}
      <div className={styles.oddsCard}>
        <span className={styles.oddsCardTitle}>{marketTitle}</span>
        <ErrorBoundary>
          <OddsMarket market={display} decimalSeparator={decimalSeparator} hideTitle />
        </ErrorBoundary>
      </div>
    </div>
  );
}

/**
 * GemWithOdds — inline/article composition: the atom + its priced market
 * beneath, from hook-shaped inputs (MatchInsight + a raw odds strip).
 */
export function GemWithOdds({
  insight,
  oddsStrip,
  variant,
}: {
  insight: MatchInsight;
  oddsStrip?: unknown;
  variant: GemVariant;
}): ReactElement | null {
  const tenant = useTenantConfig();
  const labels = useAdapterStrings();
  const decimalSeparator: '.' | ',' =
    tenant.kind === 'live' || tenant.kind === 'paused'
      ? tenant.config.market.decimalSeparator
      : '.';
  let display: DisplayMarket | null = null;
  if (oddsStrip) {
    try {
      display = stripMarketToDisplay(oddsStrip as never, { labels });
    } catch {
      display = null;
    }
  }
  const atom = (
    <DataGemCard
      eventId=""
      gem={{ ...insight, parts: insight.parts as Record<string, unknown> | null }}
      variant={variant}
    />
  );
  if (!display) return atom;
  return (
    <div className={styles.pair}>
      {atom}
      <div className={styles.oddsCard}>
        <ErrorBoundary>
          <OddsMarket market={display} decimalSeparator={decimalSeparator} hideTitle />
        </ErrorBoundary>
      </div>
    </div>
  );
}

export type { DataGemCardProps };
export type GemChildren = ReactNode;
